import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, Mail, Phone } from 'lucide-react'
import { getOrder, updateOrderStatus, updateOrderPayment } from '../api/orders'
import { getCachedCustomStatuses } from '../api/customStatuses'
import { useLangPrefix } from '../hooks/useLangPrefix'
import DesktopShell from '../components/layout/DesktopShell'
import AppShell from '../components/layout/AppShell'
import useManagerStore from '../store/managerStore'
import { formatMoney, getMinorUnits, parseMoneyInput } from '../lib/money'
import { formatAmountForInput, moneyApiErrorMessage, moneyInputErrorMessage } from '../lib/moneyInput'
import { useTenantCurrencyState } from '../hooks/useTenantCurrency'

const PAYMENT_COLORS = {
    unpaid:       { bg: '#fee2e2', color: '#dc2626' },
    deposit_paid: { bg: '#fef9c3', color: '#854d0e' },
    partial:      { bg: '#ffedd5', color: '#c2410c' },
    paid:         { bg: '#dcfce7', color: '#16a34a' },
    refunded:     { bg: '#f3f4f6', color: '#6b7280' },
    waived:       { bg: '#ede9fe', color: '#7c3aed' },
}

const STATUS_FLOW = ['pending','confirmed','in_progress','completed']
const PAYMENT_METHODS = ['cash','card','bank_transfer','momo','zalo_pay','complimentary']
const PAYMENT_STATUS_OPTIONS = ['unpaid', 'deposit_paid', 'paid']
const formatDate = d => d ? new Date(d).toLocaleString('vi-VN', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'
// An order keeps the currency it was created in (BE C14 `orders.currency`);
// legacy orders without one are read in the tenant currency.
const getOrderCurrency = (order) => (typeof order?.currency === 'string' && order.currency.trim() ? order.currency : null)
const toNumber = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
}

function StatusPill({ status, map, label }) {
    const s = map[status] || { bg: '#f3f4f6', color: '#666' }
    return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>{label}</span>
}

export default function OrderDetailPage() {
    const { t } = useTranslation()
    const { orderId } = useParams()
    const navigate = useNavigate()
    const { prefix, lang } = useLangPrefix()
    const tenantId = useManagerStore((s) => s.tenantId)
    const isDesktop = window.innerWidth >= 1024
    const { currency: tenantCurrency, isResolved: currencyResolved } = useTenantCurrencyState()

    const [order, setOrder] = useState(null)
    const [loading, setLoading] = useState(true)
    const [updatingStatus, setUpdatingStatus] = useState(false)
    const [showPaymentForm, setShowPaymentForm] = useState(false)
    const [paymentStatus, setPaymentStatus] = useState('')
    const [paymentMethod, setPaymentMethod] = useState('')
    // Raw typed text of the paid amount; null means "not edited yet".
    const [paidAmountText, setPaidAmountText] = useState(null)
    const [paymentError, setPaymentError] = useState('')
    const [savingPayment, setSavingPayment] = useState(false)
    const [customStatuses, setCustomStatuses] = useState([])

    useEffect(() => {
        if (!tenantId) {
            setCustomStatuses([])
            return
        }
        let cancelled = false
        getCachedCustomStatuses(tenantId, 'order')
            .then(items => { if (!cancelled) setCustomStatuses((Array.isArray(items) ? items : []).filter(s => !s.is_default)) })
            .catch(err => Sentry.captureException(err, { tags: { action: 'fetch_order_custom_statuses' } }))
        return () => { cancelled = true }
    }, [tenantId])

    function statusLabel(slug) {
        const custom = customStatuses.find(s => s.slug === slug)
        if (custom) return custom.label
        return t(`orders.orderStatus.${slug}`, { defaultValue: slug?.replace(/_/g, ' ') })
    }

    async function fetchOrder() {
        if (!tenantId) {
            setOrder(null)
            setLoading(false)
            return
        }
        setLoading(true)
        try {
            const data = await getOrder(tenantId, orderId)
            setOrder(data)
            const nextPaymentStatus = PAYMENT_STATUS_OPTIONS.includes(data.payment_status)
                ? data.payment_status
                : 'unpaid'
            setPaymentStatus(nextPaymentStatus)
            setPaymentMethod(data.payment_method || '')
            setPaidAmountText(null)
            setPaymentError('')
        } catch (e) {
            Sentry.captureException(e, { tags: { action: 'fetch_order' }, extra: { orderId } })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchOrder() }, [orderId, tenantId])

    async function handleStatusUpdate(newStatus) {
        if (!order || updatingStatus || !tenantId) return
        setUpdatingStatus(true)
        try {
            await updateOrderStatus(tenantId, orderId, newStatus)
            await fetchOrder()
        } catch (e) {
            Sentry.captureException(e, { tags: { action: 'update_order_status' }, extra: { orderId, newStatus } })
        } finally {
            setUpdatingStatus(false)
        }
    }

    // Order currency when stored, else the tenant currency once resolved; null
    // while unknown (money is then shown as a placeholder and inputs are disabled).
    const moneyCurrency = getOrderCurrency(order) ?? (currencyResolved ? tenantCurrency : null)
    const formatPrice = (amount) => (moneyCurrency
        ? formatMoney(amount || 0, moneyCurrency, lang)
        : <span role="status" aria-label={t('common.loading')}>…</span>)
    const paidAmount = paidAmountText ?? (moneyCurrency ? formatAmountForInput(order?.paid_amount, moneyCurrency) : '')

    const amountErrorMessage = (errorCode) => moneyInputErrorMessage(t, errorCode, moneyCurrency, lang)

    function handlePaidAmountBlur() {
        if (paidAmountText === null || !moneyCurrency) return
        const result = parseMoneyInput(paidAmountText, moneyCurrency)
        if (!result.ok) { setPaymentError(amountErrorMessage(result.error)); return }
        setPaymentError('')
        if (result.value !== null) setPaidAmountText(formatAmountForInput(result.value, moneyCurrency))
    }

    async function handlePaymentSave() {
        if (!tenantId || !moneyCurrency) return
        // An untouched amount is sent exactly as loaded (no re-validation).
        let paid = Number.isFinite(Number(order?.paid_amount)) && order?.paid_amount !== null ? Number(order.paid_amount) : null
        if (paidAmountText !== null) {
            const result = parseMoneyInput(paidAmountText, moneyCurrency)
            if (!result.ok) { setPaymentError(amountErrorMessage(result.error)); return }
            paid = result.value
        }
        setPaymentError('')
        setSavingPayment(true)
        try {
            await updateOrderPayment(tenantId, orderId, {
                payment_status: paymentStatus,
                payment_method: paymentMethod || null,
                paid_amount: paid,
            })
            setShowPaymentForm(false)
            await fetchOrder()
        } catch (e) {
            if (!e?.response || e.response.status >= 500)
                Sentry.captureException(e, { tags: { action: 'update_order_payment' }, extra: { orderId } })
            setPaymentError(moneyApiErrorMessage(t, e, moneyCurrency, lang))
        } finally {
            setSavingPayment(false)
        }
    }

    if (loading) return <div style={{ padding: 48, textAlign: 'center', color: '#999' }}>{t('orders.loadingOrder')}</div>
    if (!order) return <div style={{ padding: 48, textAlign: 'center', color: '#ef4444' }}>{t('orders.notFound')}</div>

    const currentStatusIndex = STATUS_FLOW.indexOf(order.status)
    const canAdvance = currentStatusIndex >= 0 && currentStatusIndex < STATUS_FLOW.length - 1
    const nextStatus = canAdvance ? STATUS_FLOW[currentStatusIndex + 1] : null
    const canCancel = !['completed','cancelled','refunded'].includes(order.status)
    const subtotalAmount = toNumber(order.subtotal)
    const taxAmount = toNumber(order.tax)
    const serviceChargeAmount = toNumber(order.service_charge)
    const discountType = order.discount_reason || 'none'
    const rawDiscount = toNumber(order.discount)
    const discountAmount = discountType === 'percent'
        ? rawDiscount * subtotalAmount / 100
        : discountType === 'fixed'
            ? rawDiscount
            : 0
    const normalizedDiscountAmount = Math.max(0, Math.min(discountAmount, subtotalAmount))
    const totalAmount = subtotalAmount - normalizedDiscountAmount + taxAmount + serviceChargeAmount

    const inputStyle = { width: '100%', padding: '8px 10px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: 'white' }
    const labelStyle = { fontSize: 12, color: '#555', marginBottom: 4, display: 'block', fontWeight: 500 }
    const cardStyle = { background: 'white', borderRadius: 12, border: '0.5px solid #e0e3f0', padding: 20, marginBottom: 16 }

    const content = (
        <div style={{ padding: isDesktop ? '24px 32px' : '12px 12px 80px 12px', background: 'var(--gc-page-bg)', minHeight: '100vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button onClick={() => navigate(`${prefix}/orders`)}
                        style={{ padding: '6px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, background: 'white', fontSize: 13, cursor: 'pointer', color: '#666' }}>{t('common.back')}</button>
                    <div>
                        <div style={{ fontSize: isDesktop ? 18 : 15, fontWeight: 700, color: '#111' }}>{t('orders.orderDetails')}</div>
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>#{order.id.slice(0,8).toUpperCase()} · {formatDate(order.created_at)}</div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {canCancel && (
                        <button onClick={() => { if (window.confirm(t('orders.cancelConfirm'))) handleStatusUpdate('cancelled') }}
                            style={{ padding: '7px 14px', border: '0.5px solid #fecaca', borderRadius: 8, background: 'white', fontSize: 13, cursor: 'pointer', color: '#dc2626' }}>
                            {t('orders.cancelOrder')}
                        </button>
                    )}
                    {nextStatus && (
                        <button onClick={() => handleStatusUpdate(nextStatus)} disabled={updatingStatus}
                            style={{ padding: '7px 16px', border: 'none', borderRadius: 8, background: 'var(--gc-primary)', color: 'white', fontSize: 13, fontWeight: 500, cursor: 'pointer', opacity: updatingStatus ? 0.7 : 1 }}>
                            {updatingStatus ? t('orders.updating') : t('orders.markAs', { status: statusLabel(nextStatus) })}
                        </button>
                    )}
                    {customStatuses.length > 0 && (
                        <select
                            value=""
                            disabled={updatingStatus}
                            onChange={e => { if (e.target.value) handleStatusUpdate(e.target.value) }}
                            style={{ padding: '7px 10px', border: '0.5px solid #e0e3f0', borderRadius: 8, background: 'white', fontSize: 13, color: '#333' }}
                        >
                            <option value="">{t('orders.moreStatus')}</option>
                            {customStatuses.map(s => (
                                <option key={s.slug} value={s.slug}>{s.label}</option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 300px' : '1fr', gap: 16 }}>
                <div>
                    <div style={cardStyle}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 16, paddingBottom: 10, borderBottom: '0.5px solid #f0f0f0' }}>{t('orders.progress')}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16, overflowX: 'auto' }}>
                            {STATUS_FLOW.map((s, i) => {
                                const done = STATUS_FLOW.indexOf(order.status) >= i
                                const isCurrent = order.status === s
                                return (
                                    <div key={s} style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 80 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: done ? 'var(--gc-primary)' : '#e0e3f0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4, border: isCurrent ? '2px solid var(--gc-primary)' : 'none' }}>
                                                {done ? <Check size={13} color="white" /> : <span style={{ color: '#999', fontSize: 11 }}>{i + 1}</span>}
                                            </div>
                                            <div style={{ fontSize: 10, color: done ? 'var(--gc-primary)' : '#999', fontWeight: isCurrent ? 600 : 400, textAlign: 'center' }}>{statusLabel(s)}</div>
                                        </div>
                                        {i < STATUS_FLOW.length - 1 && <div style={{ height: 2, flex: 1, background: STATUS_FLOW.indexOf(order.status) > i ? 'var(--gc-primary)' : '#e0e3f0', marginBottom: 20 }} />}
                                    </div>
                                )
                            })}
                        </div>
                        {!STATUS_FLOW.includes(order.status) && (
                            <div style={{ padding: '8px 12px', background: '#fee2e2', borderRadius: 8, fontSize: 13, color: '#dc2626', fontWeight: 500 }}>
                                {t('orders.orderCancelledBanner', {
                                    status: statusLabel(order.status),
                                    extra: order.cancelled_at ? ' · ' + formatDate(order.cancelled_at) : '',
                                })}
                            </div>
                        )}
                    </div>

                    <div style={cardStyle}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 16, paddingBottom: 10, borderBottom: '0.5px solid #f0f0f0' }}>
                            {t('orders.productsSection', { count: order.line_items?.length || 0 })}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    {[t('orders.colProduct'), t('orders.colQty'), t('orders.colUnitPrice'), t('orders.colSubtotal'), t('orders.colNotes')].map(h => (
                                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#666', borderBottom: '0.5px solid #f0f0f0' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(order.line_items || []).map((item, i) => (
                                    <tr key={i} style={{ borderBottom: '0.5px solid #f8f8f8' }}>
                                        <td style={{ padding: '10px', fontSize: 13, color: '#111', fontWeight: 500 }}>
                                            {item.product_name}
                                            {!item.product_id && (
                                                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#999', border: '0.5px solid #e0e3f0', borderRadius: 4, padding: '1px 5px' }}>
                                                    {t('orderForm.customItemTag')}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '10px', fontSize: 13, color: '#333', textAlign: 'center' }}>{item.qty}</td>
                                        <td style={{ padding: '10px', fontSize: 13, color: '#666' }}>{formatPrice(item.unit_price)}</td>
                                        <td style={{ padding: '10px', fontSize: 13, fontWeight: 600, color: 'var(--gc-primary)' }}>{formatPrice(item.subtotal)}</td>
                                        <td style={{ padding: '10px', fontSize: 12, color: '#999' }}>{item.notes || t('dashboard.dash')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ marginTop: 16, borderTop: '0.5px solid #f0f0f0', paddingTop: 12 }}>
                            {[
                                { label: t('orders.subtotal'), value: formatPrice(subtotalAmount), valueColor: '#666' },
                                { label: t('orders.discount'), value: formatPrice(normalizedDiscountAmount), valueColor: normalizedDiscountAmount > 0 ? '#dc2626' : '#666' },
                                taxAmount ? { label: t('orders.tax'), value: formatPrice(taxAmount), valueColor: '#666' } : null,
                                serviceChargeAmount ? { label: t('orders.serviceCharge'), value: formatPrice(serviceChargeAmount), valueColor: '#666' } : null,
                            ].filter(Boolean).map((row) => (
                                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#666', marginBottom: 6 }}>
                                    <span>{row.label}</span><span style={{ color: row.valueColor }}>{row.value}</span>
                                </div>
                            ))}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: '#111', marginTop: 8, paddingTop: 8, borderTop: '0.5px solid #e0e3f0' }}>
                                <span>{t('orders.total')}</span><span style={{ color: 'var(--gc-primary)' }}>{formatPrice(totalAmount)}</span>
                            </div>
                        </div>
                    </div>

                    <div style={cardStyle}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 16, paddingBottom: 10, borderBottom: '0.5px solid #f0f0f0' }}>{t('orders.timeline')}</div>
                        {(order.status_history || []).map((h, i) => (
                            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--gc-primary)', marginTop: 3, flexShrink: 0 }} />
                                    {i < (order.status_history.length - 1) && <div style={{ width: 1, flex: 1, background: '#e0e3f0', marginTop: 4 }} />}
                                </div>
                                <div style={{ paddingBottom: 8 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111', textTransform: 'capitalize' }}>{h.status ? t(`orders.orderStatus.${h.status}`, { defaultValue: h.status.replace(/_/g,' ') }) : ''}</div>
                                    {h.note && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{h.note}</div>}
                                    <div style={{ fontSize: 11, color: '#999', marginTop: 3 }}>{formatDate(h.timestamp)}{h.by ? t('orders.timelineBy', { name: h.by }) : ''}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <div style={cardStyle}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 16, paddingBottom: 10, borderBottom: '0.5px solid #f0f0f0' }}>{t('orders.customer')}</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 4 }}>{order.customer_name || t('dashboard.dash')}</div>
                        {order.customer_phone && <div style={{ fontSize: 13, color: '#666', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><Phone size={14} /> {order.customer_phone}</div>}
                        {order.customer_email && <div style={{ fontSize: 13, color: '#666', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={14} /> {order.customer_email}</div>}
                        {order.channel && <div style={{ fontSize: 12, color: '#999', marginTop: 8, textTransform: 'capitalize' }}>{t('orders.viaChannel', { channel: order.channel })}</div>}
                        {order.conversation_id && (
                            <button onClick={() => navigate(`${prefix}/inbox?user=${order.user_id}`)}
                                style={{ marginTop: 12, width: '100%', padding: '8px', border: '0.5px solid var(--gc-primary)', borderRadius: 8, background: 'white', color: 'var(--gc-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                                {t('orders.viewConversation')}
                            </button>
                        )}
                    </div>

                    <div style={cardStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 10, borderBottom: '0.5px solid #f0f0f0' }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{t('orders.payment')}</div>
                            <button onClick={() => {
                                // Closing without saving drops the typed amount and any error.
                                if (showPaymentForm) { setPaidAmountText(null); setPaymentError('') }
                                setShowPaymentForm(p => !p)
                            }}
                                style={{ fontSize: 12, color: 'var(--gc-primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
                                {showPaymentForm ? t('common.cancel') : t('common.update')}
                            </button>
                        </div>
                        {showPaymentForm ? (
                            <div>
                                <div style={{ marginBottom: 10 }}>
                                    <label style={labelStyle}>{t('orders.paymentStatus')}</label>
                                    <select style={inputStyle} value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)}>
                                        {PAYMENT_STATUS_OPTIONS.map(s => <option key={s} value={s}>{t(`orders.paymentStatusEnum.${s}`, { defaultValue: s.replace(/_/g,' ') })}</option>)}
                                    </select>
                                </div>
                                <div style={{ marginBottom: 10 }}>
                                    <label style={labelStyle}>{t('orders.paymentMethod')}</label>
                                    <select style={inputStyle} value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                                        <option value="">{t('orders.selectMethod')}</option>
                                        {/* keep a stored method that is no longer offered, so saving does not blank it */}
                                        {(PAYMENT_METHODS.includes(paymentMethod) || !paymentMethod ? PAYMENT_METHODS : [...PAYMENT_METHODS, paymentMethod])
                                            .map(m => <option key={m} value={m}>{m.replace(/_/g,' ')}</option>)}
                                    </select>
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <label htmlFor="order-paid-amount" style={labelStyle}>{t('orders.amountPaid', { currency: moneyCurrency ?? '…' })}</label>
                                    <input
                                        style={inputStyle}
                                        id="order-paid-amount"
                                        value={paidAmount}
                                        onChange={e => { setPaidAmountText(e.target.value); setPaymentError('') }}
                                        onBlur={handlePaidAmountBlur}
                                        placeholder={getMinorUnits(moneyCurrency) > 0 ? '0.00' : '0'}
                                        inputMode={getMinorUnits(moneyCurrency) > 0 ? 'decimal' : 'numeric'}
                                        disabled={!moneyCurrency}
                                        aria-invalid={Boolean(paymentError)}
                                    />
                                </div>
                                {paymentError && (
                                    <div role="alert" style={{ fontSize: 12, color: '#ef4444', marginBottom: 10 }}>{paymentError}</div>
                                )}
                                <button onClick={handlePaymentSave} disabled={savingPayment || !moneyCurrency}
                                    style={{ width: '100%', padding: '9px', border: 'none', borderRadius: 8, background: 'var(--gc-primary)', color: 'white', fontSize: 13, fontWeight: 500, cursor: 'pointer', opacity: savingPayment || !moneyCurrency ? 0.7 : 1 }}>
                                    {savingPayment ? t('common.saving') : t('orders.savePayment')}
                                </button>
                            </div>
                        ) : (
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ fontSize: 13, color: '#666' }}>{t('orders.thStatus')}</span>
                                    <StatusPill status={order.payment_status} map={PAYMENT_COLORS} label={t(`orders.paymentStatusEnum.${order.payment_status}`, { defaultValue: order.payment_status?.replace(/_/g,' ') })} />
                                </div>
                                {order.payment_method && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <span style={{ fontSize: 13, color: '#666' }}>{t('orders.method')}</span>
                                        <span style={{ fontSize: 13, color: '#333', textTransform: 'capitalize' }}>{order.payment_method.replace(/_/g,' ')}</span>
                                    </div>
                                )}
                                {order.paid_amount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 13, color: '#666' }}>{t('orders.paid')}</span>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>{formatPrice(order.paid_amount)}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {order.order_attributes && Object.keys(order.order_attributes).length > 0 && (
                        <div style={cardStyle}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 12, paddingBottom: 10, borderBottom: '0.5px solid #f0f0f0' }}>{t('orders.orderInfo')}</div>
                            {Object.entries(order.order_attributes).map(([k, v]) => (
                                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <span style={{ fontSize: 12, color: '#666', textTransform: 'capitalize' }}>{k.replace(/_/g,' ')}</span>
                                    <span style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>{String(v)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {order.internal_notes && (
                        <div style={cardStyle}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 8 }}>{t('orders.internalNotes')}</div>
                            <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>{order.internal_notes}</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )

    return isDesktop
        ? <DesktopShell>{content}</DesktopShell>
        : <AppShell activeTab="orders">{content}</AppShell>
}
